import React, { createContext, useContext, useEffect, useState } from "react";

type AuthState = {
  loading: boolean;
  isAuthenticated: boolean;
  email?: string;
  idToken?: string;
  login: (tokenData: {
    idToken: string;
    accessToken: string;
    refreshToken: string;
    email?: string;
  }) => void;
  logout: () => void;
  refresh: () => void;
};

const AuthCtx = createContext<AuthState>({
  loading: true,
  isAuthenticated: false,
  login: () => {},
  logout: () => {},
  refresh: () => {},
});

let logoutTimer: NodeJS.Timeout | null = null;

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [loading, setLoading] = useState(true);
  const [isAuthenticated, setAuthed] = useState(false);
  const [email, setEmail] = useState<string>();
  const [idToken, setIdToken] = useState<string>();

  // --- Helper: reset inactivity timer
  function setupInactivityTimer(onTimeout: () => void, timeoutMs = 10 * 60 * 1000) {
    const resetTimer = () => {
      if (logoutTimer) clearTimeout(logoutTimer);
      logoutTimer = setTimeout(onTimeout, timeoutMs);
    };

    window.addEventListener("mousemove", resetTimer);
    window.addEventListener("keydown", resetTimer);
    window.addEventListener("click", resetTimer);
    window.addEventListener("scroll", resetTimer);

    resetTimer();

    return () => {
      if (logoutTimer) clearTimeout(logoutTimer);
      window.removeEventListener("mousemove", resetTimer);
      window.removeEventListener("keydown", resetTimer);
      window.removeEventListener("click", resetTimer);
      window.removeEventListener("scroll", resetTimer);
    };
  }

  const refresh = () => {
    const stored = sessionStorage.getItem("idToken");
    if (stored) {
      setIdToken(stored);
      const payload = JSON.parse(atob(stored.split(".")[1]));
      setEmail(payload.email || payload.username || undefined);
      setAuthed(true);
    } else {
      setIdToken(undefined);
      setEmail(undefined);
      setAuthed(false);
    }
    setLoading(false);
  };

  useEffect(() => {
    refresh();
  }, []);

  useEffect(() => {
    if (isAuthenticated) {
      const cleanup = setupInactivityTimer(() => {
        console.log("Auto-logout due to inactivity");
        logout();
      });
      return cleanup;
    }
  }, [isAuthenticated]);

  const login = (tokens: {
    idToken: string;
    accessToken: string;
    refreshToken: string;
    email?: string;
  }) => {
    sessionStorage.setItem("idToken", tokens.idToken);
    sessionStorage.setItem("accessToken", tokens.accessToken);
    sessionStorage.setItem("refreshToken", tokens.refreshToken);
    if (tokens.email) setEmail(tokens.email);
    setAuthed(true);
    setIdToken(tokens.idToken);
  };

  const logout = () => {
    sessionStorage.clear();
    setAuthed(false);
    setEmail(undefined);
    setIdToken(undefined);
  };

  return (
    <AuthCtx.Provider
      value={{ loading, isAuthenticated, email, idToken, login, logout, refresh }}
    >
      {children}
    </AuthCtx.Provider>
  );
}

export function useAuth() {
  return useContext(AuthCtx);
}
